import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  of,
  switchMap,
  catchError,
  shareReplay,
  startWith,
  map,
} from 'rxjs';

import { AuthFacade } from '../../auth/auth.facade';
import { AccessRequestsService } from '../../api/access-requests.service';
import { WorldsService, WorldSummary } from '../../api/worlds.service';
import { MeService } from '../../api/me.service';
import { MeResponse } from '../../api/api.types';

type WorkspaceHomeVm =
  | { state: 'unauthenticated' }
  | { state: 'loading' }
  | { state: 'request-access'; email: string | null; approvalStatus: string }
  | { state: 'dashboard'; worlds: WorldSummary[]; loading: boolean; error: string | null };

@Component({
  selector: 'of-workspace-home-page',
  standalone: true,
  imports: [CommonModule],
  template: `
    <ng-container *ngIf="vm$ | async as vm">
      <ng-container *ngIf="isUnauthenticated(vm); else notUnauth">
        <section class="request-access">
          <div class="card request-card">
            <h2>Sign in to continue</h2>
            <p>Use your authorized account to open the Omniscient Forge workspace.</p>
            <button type="button" class="btn-primary-gradient" (click)="signIn()">
              Launch Workspace
            </button>
          </div>
        </section>
      </ng-container>

      <ng-template #notUnauth>
        <ng-container *ngIf="isRequestAccess(vm); else notRequest">
          <section class="request-access">
            <div class="card request-card">
              <div class="hero-logo compact">
                <span aria-hidden="true">&#10022;</span>
                <span>Beta Access</span>
              </div>
              <h2>
                {{ vm.approvalStatus === 'denied' ? 'Access denied' : 'Omniscient Forge is in beta' }}
              </h2>
              <p>
                {{
                  vm.approvalStatus === 'denied'
                    ? 'Your request was reviewed but access is currently denied. You may request access again if your status changes.'
                    : (vm.email || 'Your account') +
                      ' is registered, but the workspace is limited to approved beta testers. Request access and we will review it soon.'
                }}
              </p>

              <div *ngIf="accessRequestError" class="form-alert error">{{ accessRequestError }}</div>
              <div *ngIf="accessRequestSubmitted" class="form-alert success">
                {{
                  accessRequestAlreadyOnFile
                    ? 'Your access request is already on file.'
                    : 'Your access request has been sent to the global admin.'
                }}
              </div>
              <button
                *ngIf="!accessRequestSubmitted"
                type="button"
                class="btn-primary-gradient"
                [disabled]="accessRequestPending"
                (click)="requestAccess()"
              >
                {{
                  accessRequestPending
                    ? 'Requesting...'
                    : vm.approvalStatus === 'denied'
                      ? 'Request access again'
                      : 'Request Access'
                }}
              </button>
              <p class="support-text">
                The global admin is notified and will approve access manually.
              </p>
            </div>
          </section>
        </ng-container>
      </ng-template>

      <ng-template #notRequest>
        <ng-container *ngIf="isDashboard(vm); else loadingState">
          <section class="dashboard-grid">
            <aside class="worlds-panel">
              <div class="worlds-panel__cta">
                <button type="button" class="btn-primary-gradient" (click)="createWorld()">
                  Forge new world
                </button>
              </div>
              <div class="worlds-panel__list">
                <p class="kicker">Worlds</p>
                <p *ngIf="!vm.loading && vm.worlds.length === 0" class="support-text">No worlds yet.</p>
                <ul *ngIf="vm.worlds.length > 0" class="worlds-nav">
                  <li *ngFor="let world of vm.worlds">
                    <button
                      type="button"
                      [class.active]="lastOpenedWorldId && lastOpenedWorldId === world.id"
                      (click)="openWorld(world.id)"
                    >
                      <span class="world-name">{{ world.name }}</span>
                      <span class="world-meta">{{ world.systemType }}</span>
                    </button>
                  </li>
                </ul>
              </div>
            </aside>

            <section class="content-area">
              <div class="section-heading">
                <div>
                  <p class="kicker">Recent Projects</p>
                  <h3>Active Realms</h3>
                </div>
                <button
                  type="button"
                  class="btn-outline slim"
                  (click)="refreshWorlds()"
                  [disabled]="vm.loading"
                >
                  {{ vm.loading ? 'Refreshing...' : 'Refresh' }}
                </button>
              </div>

              <ng-container *ngIf="vm.loading && vm.worlds.length === 0">
                <div class="projects-grid">
                  <div class="project-card shimmer" *ngFor="let placeholder of placeholders">
                    <div class="project-title placeholder"></div>
                    <div class="project-line placeholder"></div>
                    <div class="project-line placeholder short"></div>
                  </div>
                </div>
              </ng-container>

              <div *ngIf="vm.error" class="project-card error-card">
                <p>{{ vm.error }}</p>
                <button type="button" class="btn-outline" (click)="refreshWorlds()">Try again</button>
              </div>

              <div *ngIf="!vm.loading && !vm.error && vm.worlds.length === 0" class="project-card empty-state">
                <span aria-hidden="true">&#10022;</span>
                <h4>No worlds yet</h4>
                <p>
                  Draft your first realm to start syncing Omniscient Forge data. Every world keeps its own presets,
                  timelines, and sync status.
                </p>
                <button type="button" class="btn-primary-gradient" (click)="createWorld()">Create a world</button>
              </div>

              <div *ngIf="vm.worlds.length > 0" class="projects-grid" id="project-list">
                <div class="project-card" *ngFor="let world of vm.worlds">
                  <div class="project-card__header">
                    <div>
                      <div class="project-title">{{ world.name }}</div>
                      <div class="project-slug">{{ world.slug }}</div>
                    </div>
                    <span class="project-date">{{ world.createdAt | date: 'shortDate' }}</span>
                  </div>
                  <div class="project-meta">
                    <span>System &middot; {{ world.systemType }}</span>
                    <span>Era &middot; {{ world.era || 'Unknown' }}</span>
                    <span>Tech &middot; {{ world.techLevel || 'Unknown' }}</span>
                  </div>
                  <div class="project-card__footer">
                    <div class="badge-linked">
                      <span aria-hidden="true">&#10022;</span>
                      {{ badgeText(world) }}
                    </div>
                    <button type="button" class="btn-outline" (click)="openWorld(world.id)">
                      Open world
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </section>
        </ng-container>
      </ng-template>

      <ng-template #loadingState>
        <section class="request-access">
          <div class="card request-card">
            <p class="support-text">Preparing your forge...</p>
          </div>
        </section>
      </ng-template>
    </ng-container>
  `,
})
export class WorkspaceHomePage {
  private readonly refresh$ = new BehaviorSubject<void>(undefined);
  private readonly worldsRefresh$ = new BehaviorSubject<void>(undefined);
  private readonly lastOpenWorldKey = 'omniforge:lastWorldId';

  vm$: Observable<WorkspaceHomeVm>;
  placeholders = Array.from({ length: 3 });
  lastOpenedWorldId: string | null = null;

  accessRequestPending = false;
  accessRequestSubmitted = false;
  accessRequestAlreadyOnFile = false;
  accessRequestError: string | null = null;

  constructor(
    private readonly auth: AuthFacade,
    private readonly router: Router,
    private readonly meService: MeService,
    private readonly accessRequests: AccessRequestsService,
    private readonly worlds: WorldsService
  ) {
    if (typeof window !== 'undefined') {
      this.lastOpenedWorldId = window.localStorage.getItem(this.lastOpenWorldKey);
    }

    const me$ = this.refresh$.pipe(
      switchMap(() => {
        if (!this.auth.isAuthenticated()) {
          return of(null as MeResponse | null);
        }
        return this.meService.getMe(true).pipe(catchError(() => of(null)));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const worldsState$ = this.worldsRefresh$.pipe(
      switchMap(() =>
        this.worlds.list().pipe(
          map((res) => ({
            loading: false,
            error: null as string | null,
            worlds: res.worlds ?? [],
          })),
          catchError(() =>
            of({
              loading: false,
              error: 'Unable to load worlds',
              worlds: [] as WorldSummary[],
            })
          ),
          startWith({ loading: true, error: null as string | null, worlds: [] as WorldSummary[] })
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.vm$ = me$.pipe(
      switchMap((me) => {
        if (!this.auth.isAuthenticated()) {
          return of({ state: 'unauthenticated' } as WorkspaceHomeVm);
        }

        if (!me) {
          return of({ state: 'loading' } as WorkspaceHomeVm);
        }

        const canAccessWorkspace =
          me.user.role === 'global_admin' || (me.user.role === 'gm' && me.user.isApproved);

        if (!canAccessWorkspace) {
          return of({
            state: 'request-access',
            email: me.user.email,
            approvalStatus: me.user.approvalStatus,
          } as WorkspaceHomeVm);
        }

        return worldsState$.pipe(
          map((worldState) => ({
            state: 'dashboard',
            worlds: worldState.worlds,
            loading: worldState.loading,
            error: worldState.error,
          }) as WorkspaceHomeVm)
        );
      })
    );
  }

  signIn() {
    this.auth.loginToWorkspace();
  }

  requestAccess() {
    if (this.accessRequestPending) return;
    this.accessRequestPending = true;
    this.accessRequestError = null;
    this.accessRequests.requestAccess().subscribe({
      next: (res) => {
        this.accessRequestPending = false;
        this.accessRequestSubmitted = true;
        this.accessRequestAlreadyOnFile = !!res.alreadyRequested;
        this.refresh$.next();
      },
      error: () => {
        this.accessRequestPending = false;
        this.accessRequestError = 'Unable to request access right now.';
      },
    });
  }

  refreshWorlds() {
    this.worldsRefresh$.next();
  }

  createWorld() {
    void this.router.navigate(['/workspace/worlds/new']);
  }

  openWorld(worldId: string) {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(this.lastOpenWorldKey, worldId);
    }
    this.lastOpenedWorldId = worldId;
    void this.router.navigate(['/workspace/worlds', worldId]);
  }

  badgeText(world: WorldSummary): string {
    const status = (world.status || '').toLowerCase();
    const isLinked = status.includes('sync') || status.includes('link');
    return isLinked ? 'Linked to Foundry' : world.status || 'Draft';
  }

  isUnauthenticated(vm: WorkspaceHomeVm): vm is { state: 'unauthenticated' } {
    return vm.state === 'unauthenticated';
  }

  isRequestAccess(vm: WorkspaceHomeVm): vm is { state: 'request-access'; email: string | null; approvalStatus: string } {
    return vm.state === 'request-access';
  }

  isDashboard(vm: WorkspaceHomeVm): vm is { state: 'dashboard'; worlds: WorldSummary[]; loading: boolean; error: string | null } {
    return vm.state === 'dashboard';
  }
}
